// Canonical date-range presets, shared by the DateRangePresets picker AND the
// data layer (backOfficeApi) so the dashboard's startup prefetch and the page's
// own load resolve to the SAME window — otherwise two callers compute "last 30
// days" two different ways and the headline flashes between two totals.
//
// Presets are a "since X" LOWER bound only: `to` is null. The window is
// open-ended on purpose — bounding `to` to now would hide future-dated records
// (upcoming events, scheduled payouts), and every consuming backend applies
// `from` on its own (finance-stats, /users, /users/customers all treat `to` as
// an optional upper bound). So we never send a `to` for a relative preset.

export type DatePreset = { id: string; label: string };
export type DateWindow = { from: string | null; to: string | null };

export const DATE_PRESETS: DatePreset[] = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'mtd', label: 'This month' },
  { id: 'ytd', label: 'This year' },
];

const DAY = 86400000;

/**
 * UTC, because the reports engine groups in UTC — a bound in another zone lands mid-bucket and
 * the first bucket of the window comes back a sliver wearing a whole day's label. Anchored here
 * it falls exactly on a bucket boundary. (Prod 2026-09-08: bucket 2026-09-01 read UGX 2,044,400
 * in the 30d window and 133,000 in 7d, whose bound was local midnight = 2026-09-01T21:00Z.)
 *
 * Timestamps are served as instants, so anything showing a raw time can render it locally.
 */
const startOfUTCDay = (d: Date): Date => { const x = new Date(d); x.setUTCHours(0, 0, 0, 0); return x; };

// Resolve a preset id to an ISO { from, to } window (relative to now).
// `to` is always null for relative presets (open-ended "since from").
export const resolveDatePreset = (id: string): DateWindow => {
  const now = new Date();
  let from: Date;
  switch (id) {
    case 'today': from = startOfUTCDay(now); break;
    case '7d':    from = startOfUTCDay(new Date(now.getTime() - 6 * DAY)); break;
    case '30d':   from = startOfUTCDay(new Date(now.getTime() - 29 * DAY)); break;
    case '90d':   from = startOfUTCDay(new Date(now.getTime() - 89 * DAY)); break;
    case 'mtd':   from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)); break;
    case 'ytd':   from = new Date(Date.UTC(now.getUTCFullYear(), 0, 1)); break;
    default:      return { from: null, to: null }; // 'all'
  }
  return { from: from.toISOString(), to: null };
};

// ─────────────────────────────────────────────────────────────────────────────
// The COMPACT window strip (WindowToggle) — the same presets, short labels.
//
// The strip used to carry its OWN table in ui/charts.jsx (7d/30d/90d/All), which is why
// "Today" existed in the dropdown here and nowhere in the strip: two tables, one of them
// forgotten. The strip is now a SUBSET of DATE_PRESETS, so a preset added here reaches both
// switchers and neither can drift from the other again.
export const COMPACT_WINDOW_IDS = ['today', '7d', '30d', '90d', 'all'] as const;

// Short forms for the strip's tight chrome. Anything absent falls back to its full label.
const COMPACT_LABELS: Record<string, string> = {
  today: 'Today', '7d': '7d', '30d': '30d', '90d': '90d', 'all': 'All',
};

export const COMPACT_WINDOWS: { id: string; label: string }[] = COMPACT_WINDOW_IDS.map((id) => ({
  id,
  label: COMPACT_LABELS[id] || (DATE_PRESETS.find((p) => p.id === id)?.label ?? id),
}));

/**
 * WHERE A SEARCH STARTS LOOKING: the last 30 days — the buyer on the phone about this week. The
 * widest a search can reach is the last year ('all' sends no bound and the server applies its year,
 * tkt-backend utils/prefixSearch), one click away in the same control. Every search box starts here.
 */
export const DEFAULT_SEARCH_WINDOW = '30d';

// The absolute LOWER BOUND for a window id — the one thing a caller should ever send.
//
// Read this before reaching for presetDays(): a DAY COUNT IS NOT A WINDOW, and converting
// one back into a date is where "select Today, get yesterday" came from. Three separate
// callers did `new Date(Date.now() - days * 86400000)`, which is N×24h ago — a ROLLING
// window anchored to the current clock time, not a calendar one anchored to midnight.
// Measured at 07:59 EAT on 2 Aug: presetDays('today') is 1 (today is one calendar day), and
// `now - 1 day` is 1 Aug 07:59 — so "Today" asked the backend for the last 27 hours, all of
// yesterday morning onward included. resolveDatePreset already anchors to local midnight.
//
// It is also the only form that is CACHE-SAFE. `days=1` is the same string today as it was
// yesterday, so /api/reports/preview/cached?q={…"days":1…} is a byte-identical URL from one
// day to the next and the edge/browser HTTP cache will happily serve yesterday's computed
// body. An absolute from-date changes when the day changes, so the key rolls over by itself.
export const windowFrom = (id: string): string | null => resolveDatePreset(id).from;

// Preset → a DAY COUNT. ONLY for a backend whose parameter genuinely is a count of days.
// NEVER convert the result back into a date — use windowFrom(id) for that (see above).
// Derived from resolveDatePreset so it can't disagree with the real window: the count is the
// number of whole days the preset spans, inclusive of today.
// 'all' is 0, which every caller already reads as "no window".
export const presetDays = (id: string): number => {
  const { from } = resolveDatePreset(id);
  if (!from) return 0;
  // Same zone as the bound it is measuring — mixing UTC and local here is how a "7 day" window
  // counts 8 for three hours every morning.
  const ms = startOfUTCDay(new Date()).getTime() - startOfUTCDay(new Date(from)).getTime();
  return Math.max(1, Math.round(ms / DAY) + 1);
};

// HOW MUCH OF THE NAMED PERIOD HAS ACTUALLY HAPPENED.
//
// "Today" at 02:21 is two hours of trading, and the page said only "today". A finance
// operator read UGX 38,000 from one event against days that normally collect 2.6–3.9M and
// concluded the platform was broken; the figure was exactly right for the window it was
// asked for. A number scoped to a period that is still running has no denominator on screen,
// so it is unreadable at the start of that period and indistinguishable from a crash.
//
// Only the TO-DATE presets need this: today/mtd/ytd are anchored to a boundary and run to
// now, so early in the period they cover a sliver of what their name implies. 7d/30d/90d are
// whole rolling windows (six full days plus today), and 'all' has no boundary at all.
// Returns null for those — the caller renders nothing rather than noise.
const TO_DATE = new Set(['today', 'mtd', 'ytd']);

export const windowElapsed = (id: string, now: Date = new Date()): string | null => {
  if (!TO_DATE.has(id)) return null;
  const { from } = resolveDatePreset(id);
  if (!from) return null;
  const hours = (now.getTime() - new Date(from).getTime()) / 3600000;
  if (id === 'today') {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m so far`;
  }
  const days = Math.floor(hours / 24) + 1;
  return `${days} day${days === 1 ? '' : 's'} so far`;
};

// Prose for a window, for subtitles like "· platform · last 30d".
//
// Every strip composed this by hand as `last ${id}`, which is fine for 7d/30d/90d and wrong
// the moment any other preset exists: adding "today" made four surfaces read "last today".
// Phrasing belongs with the presets, so a new preset arrives with its own wording instead of
// breaking the sentence at each call site.
export const windowPhrase = (id: string): string => {
  switch (id) {
    case 'all':   return 'all-time';
    case 'today': return 'today';
    case 'mtd':   return 'this month';
    case 'ytd':   return 'this year';
    default:      return `last ${id}`;
  }
};
